import { useEffect, useState } from "react";
import { usePostHog } from "@posthog/react";
import { useLocation, useNavigate } from "react-router-dom";

import type {
  BillingPeriod,
  UpgradeReason,
} from "@sill/shared/components/billing/pricing-copy";

import { onPaywallTriggered } from "./http-paywall-interceptor";
import { UpgradeModal } from "./UpgradeModal";
import { UpgradeSheet } from "./UpgradeSheet";
import {
  getUpgradeIntent,
  stripUpgradeIntentFromPath,
} from "../../lib/upgrade-intent";

// GlobalUpgradeSheet is the app-wide paywall orchestrator. Despite the name
// (kept for callsite stability), it now manages TWO stacked surfaces:
//   1. <UpgradeModal>   — focused single-action upsell that opens FIRST
//                         whenever onPaywallTriggered fires.
//   2. <UpgradeSheet>   — full pricing comparison + Clerk checkout, opened
//                         when the user clicks "Upgrade to Plus" or
//                         "Compare all plans" inside the modal.
//
// Mount ONCE near the React tree root (App.tsx, below BrowserRouter so
// navigation-triggered fetches are covered). All paywall callers
// (the http-paywall-interceptor
// 403 inspector, the Add-custom-alert button, the chat 20-msg rate limit,
// etc.) just fire `triggerPaywall(reason)` and this component picks the
// right UI sequence. Eliminates the need for per-callsite local
// UpgradeSheet instances.
export function GlobalUpgradeSheet() {
  // `reason` doubles as both the open flag (non-null = something is open)
  // and the contextual copy key for the modal + sheet. `showSheet` flips
  // when the user promotes from the modal to the deeper comparison
  // surface so the two are mutually exclusive (no stacked dialogs).
  const [reason, setReason] = useState<UpgradeReason | null>(null);
  const [showSheet, setShowSheet] = useState(false);
  const [initialBillingPeriod, setInitialBillingPeriod] = useState<
    BillingPeriod | undefined
  >(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const urlIntent = isProductPath(location.pathname)
    ? getUpgradeIntent(location.search)
    : null;
  const activeReason: UpgradeReason = urlIntent
    ? "pricing_page"
    : (reason ?? "pricing_page");
  const activeShowSheet = Boolean(urlIntent) || showSheet;
  const activeInitialBillingPeriod =
    urlIntent?.billingPeriod ?? initialBillingPeriod;

  useEffect(() => {
    return onPaywallTriggered((next) => {
      setReason(next);
      setInitialBillingPeriod(undefined);
      // Reset to the modal-first state even if a sheet was already open —
      // a fresh paywall event for a different reason should always start
      // at the focused upsell, not skip straight to comparison.
      setShowSheet(false);
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("checkout_returned") !== "1") return;

    posthog?.capture("sill_plus.checkout_returned", {
      surface: "consumer",
    });
    params.delete("checkout_returned");
    const search = params.toString();
    navigate(
      `${location.pathname}${search ? `?${search}` : ""}${location.hash}`,
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate, posthog]);

  const closeAll = () => {
    if (urlIntent) {
      navigate(
        stripUpgradeIntentFromPath(
          location.pathname,
          location.search,
          location.hash,
        ),
        { replace: true },
      );
    }
    setReason(null);
    setShowSheet(false);
    setInitialBillingPeriod(undefined);
  };
  const promoteToSheet = () => {
    setShowSheet(true);
  };

  return (
    <>
      <UpgradeModal
        feature={activeReason}
        open={reason !== null && !activeShowSheet}
        onOpenChange={(open) => {
          if (!open) closeAll();
        }}
        onUpgradeClick={promoteToSheet}
        onComparePlansClick={promoteToSheet}
      />
      <UpgradeSheet
        onClose={closeAll}
        open={activeShowSheet}
        reason={activeReason}
        initialBillingPeriod={activeInitialBillingPeriod}
      />
    </>
  );
}

function isProductPath(pathname: string): boolean {
  return (
    pathname === "/dashboard" ||
    pathname.startsWith("/dashboard/") ||
    pathname === "/calculators" ||
    pathname.startsWith("/calculators/") ||
    pathname.startsWith("/properties/") ||
    pathname === "/help"
  );
}
